/**
 * Consent layer (spec §5.2). GDPR/AEPD posture: nothing ships an analytics request
 * until the visitor grants; decline (or silence) keeps both sinks fully idle.
 * The Consent Mode v2 default-denied snippet lives in the prerendered <head>
 * (scripts/prerender.mjs) so it runs before any tag; this module owns the stored
 * choice, the banner UI, and the grant/revoke transitions at runtime.
 */

export type Consent = 'granted' | 'denied'

const KEY = 'shinro-consent'
const EVENT = 'consentchange'

export function getConsent(): Consent | null {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'granted' || v === 'denied' ? v : null
  } catch {
    return null
  }
}

export function setConsent(value: Consent): void {
  try { localStorage.setItem(KEY, value) } catch { /* private mode: session-only choice */ }
  document.dispatchEvent(new CustomEvent<Consent>(EVENT, { detail: value }))
}

export function onConsentChange(cb: (value: Consent) => void): () => void {
  const handler = (e: Event): void => cb((e as CustomEvent<Consent>).detail)
  document.addEventListener(EVENT, handler)
  return () => document.removeEventListener(EVENT, handler)
}

const BTN =
  "min-height:36px;padding:7px 14px;border-radius:7px;font-family:'Geist',sans-serif;font-size:13px;font-weight:500;cursor:pointer;"

function banner(): HTMLElement {
  const el = document.createElement('div')
  el.setAttribute('data-consent-banner', '')
  el.setAttribute('role', 'region')
  el.setAttribute('aria-label', 'Analytics consent')
  el.style.cssText =
    'position:fixed;left:16px;bottom:16px;z-index:60;max-width:320px;padding:14px 16px;' +
    'background:var(--hud-bg, rgba(7,10,8,0.92));border:1px solid var(--border-idle, #1E2A24);border-radius:10px;' +
    "font-family:'Geist',sans-serif;font-size:13px;line-height:1.5;color:var(--text-secondary, #97A79D);" +
    'backdrop-filter:blur(6px);'
  const text = document.createElement('p')
  text.style.cssText = 'margin:0 0 10px;'
  text.textContent = 'We use privacy-respecting analytics to improve this site. '
  const privacy = document.createElement('a')
  privacy.href = '/privacy'
  privacy.textContent = 'Privacy policy'
  privacy.style.cssText = 'color:var(--text-mute, #66756C);'
  text.appendChild(privacy)
  const row = document.createElement('div')
  row.style.cssText = 'display:flex;gap:8px;'
  const accept = document.createElement('button')
  accept.textContent = 'Accept'
  accept.style.cssText = BTN + 'background:var(--signal, #2BE07A);border:none;color:var(--on-signal, #052012);font-weight:600;'
  const decline = document.createElement('button')
  decline.textContent = 'Decline'
  decline.style.cssText = BTN + 'background:transparent;border:1px solid var(--border-idle, #1E2A24);color:var(--text-secondary, #97A79D);'
  accept.addEventListener('click', () => { setConsent('granted'); el.remove() })
  decline.addEventListener('click', () => { setConsent('denied'); el.remove() })
  row.append(accept, decline)
  el.append(text, row)
  return el
}

/**
 * Show the banner when no stored choice exists. Also binds any
 * [data-consent-manage] button to reopen the banner. The binding is delegated on
 * document rather than attached per element, because the button exists on two
 * surfaces mounted at different times: the prerendered /privacy page (present at
 * this call) and the canvas PrivacyModal (mounted later, after the deferred
 * engine boot) — a querySelectorAll pass here would miss the latter entirely.
 * Suppressed in vtest mode (visual snapshots must not include transient chrome).
 */
export function mountConsentUI(): void {
  document.addEventListener('click', (e) => {
    const t = e.target
    if (!(t instanceof Element) || !t.closest('[data-consent-manage]')) return
    if (!document.querySelector('[data-consent-banner]')) document.body.appendChild(banner())
  })
  // vtest suppression must read the URL, not the data-vtest attribute: the attribute
  // is set by the (async) mobile boot chunk, which loses the race against this call.
  if (document.documentElement.hasAttribute('data-vtest')) return
  if (new URLSearchParams(location.search).get('vtest') === '1') return
  if (getConsent() === null && !document.querySelector('[data-consent-banner]')) {
    document.body.appendChild(banner())
  }
}
